/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

// Chakra imports
import {
    Box,
    Icon,
    IconButton,
    Spinner,
    SimpleGrid,
    useDisclosure,
    ModalOverlay,
    FormControl,
    FormLabel,
    FormErrorMessage,
    useColorModeValue,
    Input,
    Textarea,
    Button,
    Modal,
    ModalBody,
    ModalHeader,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    Text,
    Flex,
    Center,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    Checkbox,
    useToast,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
    MdPerson,
    MdArrowCircleLeft,
    MdAddCircleOutline,
} from "react-icons/md";
import { DeleteIcon, InfoIcon } from "@chakra-ui/icons";
import { useParams, useNavigate } from "react-router-dom";
import {
    useGetCurrentUserQuery,
    useGetPrescriptionsByPatientIdQuery,
    useCreatePatientPrescriptionMutation,
    useDeletePatientPrescriptionMutation,
    useGetUsersQuery,
    useGetPatientQuery,
} from "store";
import { Helmet } from "react-helmet-async";
import { differenceInYears } from "date-fns";
import moment from "moment";
import PageTitle from "components/title";

function PaginatedTable({ columns, initialPageSize = 10 }) {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const { patientId } = useParams();

    const {
        data: prescriptions,
        isFetching,
        error,
    } = useGetPrescriptionsByPatientIdQuery(
        { patientId, page: pageIndex + 1, pageSize },
        { keepPreviousData: true }
    );

    // Safely handle undefined prescriptions and default to last page as 0
    const lastPage =
        prescriptions && prescriptions.count
            ? Math.ceil(prescriptions.count / pageSize) - 1
            : 0;
    const currentPage = pageIndex + 1;

    // if (error) return <Text color="red.500">Error: {error.message}</Text>;
    if (isFetching) return <Spinner size="lg" />;

    return (
        <Box overflowX="auto">
            <Table variant="striped" colorScheme="teal">
                <Thead>
                    <Tr>
                        {columns.map((column) => (
                            <Th key={column.id}>{column.header}</Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {prescriptions &&
                    prescriptions.results &&
                    prescriptions.results.length > 0 ? (
                        prescriptions.results.map((row, i) => (
                            <Tr key={i}>
                                {columns.map((column) => (
                                    <Td key={column.id}>
                                        {typeof column.accessor === "function"
                                            ? column.accessor(row)
                                            : row[column.accessor]}
                                    </Td>
                                ))}
                            </Tr>
                        ))
                    ) : (
                        <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                                No prescriptions available
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
            <Flex justifyContent="space-between" alignItems="center" p="4">
                <Button
                    onClick={() => setPageIndex(0)}
                    disabled={pageIndex === 0}
                >
                    {"<<"}
                </Button>
                <Button
                    onClick={() => setPageIndex((old) => Math.max(old - 1, 0))}
                    disabled={pageIndex === 0}
                >
                    {"<"}
                </Button>
                <Text>
                    Page {currentPage} of {lastPage + 1}
                </Text>
                <span>
                    | Go to page:{" "}
                    <Input
                        type="number"
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            setPageIndex(page);
                        }}
                        style={{ width: "100px" }}
                    />
                </span>
                <Button
                    onClick={() =>
                        setPageIndex((old) => Math.min(old + 1, lastPage))
                    }
                    disabled={pageIndex >= lastPage}
                >
                    {">"}
                </Button>
                <Button
                    onClick={() => setPageIndex(lastPage)}
                    disabled={pageIndex >= lastPage}
                >
                    {">>"}
                </Button>
                <Select
                    ml="4"
                    width="auto"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to the first page after changing page size
                    }}
                >
                    {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </Select>
            </Flex>
        </Box>
    );
}

function Prescriptions() {
    const { patientId } = useParams();
    const {
        data: prescriptions,
        error,
        isLoading,
    } = useGetPrescriptionsByPatientIdQuery({
        patientId,
        page: 1,
        pageSize: 10,
    });
    const { data: patient, isLoading: isLoadingPatient } = useGetPatientQuery(patientId);
    const prescriptionCount = prescriptions ? prescriptions.count : 0;
    const {
        data: currentUser,
        error: err,
        isLoading: isFetching,
    } = useGetCurrentUserQuery();
    const [prescriptionToDelete, setPrescriptionToDelete] = useState(null);
    const [
        deletePrescription,
        { isLoading: isDeleting },
    ] = useDeletePatientPrescriptionMutation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const toast = useToast();

    const handleDetailsClick = (presId) => {
        // console.log("Prescription ID for detail:", presId);
        navigate(`${presId}/details`);
    };

    const handleDeleteClick = (presId) => {
        setPrescriptionToDelete(presId);
        onOpen();
    };

    const confirmDelete = async () => {
        try {
            if (!prescriptionToDelete) return;

            await deletePrescription({
                patientId,
                presId: prescriptionToDelete,
            }).unwrap();
            toast({
                title: "Prescription deleted",
                description: "The prescription has been removed.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            setPrescriptionToDelete(null);
            onClose();
        } catch (error) {
            toast({
                title: "Error deleting prescription",
                description:
                    error?.data?.detail ||
                    error?.error ||
                    "Could not delete prescription.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const [
        createPrescription,
        results,
    ] = useCreatePatientPrescriptionMutation();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
    } = useForm();

    React.useEffect(() => {
        setValue("patient", patientId);
    }, [patientId, setValue]);

    const onSubmit = async (data) => {
        try {
            const payload = { ...data };
            await createPrescription({
                patientId,
                newPrescription: payload,
            }).unwrap();

            toast({
                title: "Prescription created",
                description: "Your new prescription has been saved.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            reset();
            handleClose();
        } catch (error) {
            toast({
                title: "Error",
                description:
                    error?.data?.detail ||
                    error?.error ||
                    "Failed to create prescription.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleBacktoPatientDetails = () => {
        navigate(`/patients/${patientId}/details`);
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Handle empty string case
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // useEffect(() => {
    //     if (isFetching) {
    //         console.log("Loading current user data...");
    //     }

    //     if (err) {
    //         console.error("Error fetching current user:", err);
    //     }

    //     if (currentUser) {
    //         console.log("Current User:", currentUser);
    //         console.log("Current User ID:", currentUser.id);
    //     }
    // }, [currentUser, err, isFetching]);

    // Fetch users data
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();

    // Log users data for debugging
    // console.log("Fetched users:", users);

    const userMap = useMemo(() => {
        const map = {};
        if (users && users.results) {
            // Make sure you access the results array correctly.
            users.results.forEach((user) => {
                if (user.id && user.username) {
                    map[user.id] = user.username;
                }
            });
        }
        return map;
    }, [users]); // Make sure 'users' contains the fetched data array.

    // console.log("User Map:", userMap);

    const columns = useMemo(
        () => [
            {
                id: "date",
                header: "Date",
                accessor: (row) => moment(row.date).format("MMMM D, YYYY"), // Knowing row.date is the correct path
            },
            {
                id: "time",
                header: "Time",
                accessor: (row) =>
                    moment(row.time, "HH:mm:ss.SSSSSS").format("hh:mm A"), // Knowing row.time is the correct path
            },
            {
                id: "notes",
                header: "Notes",
                accessor: (row) => row.notes, // Directly accessing the notes
            },
            {
                id: "repeat",
                header: "Repeat",
                accessor: (row) => (row.repeat ? "Yes" : "No"), // Knowing row.repeat is a boolean
            },
            {
                id: "enter_by",
                header: "Entered By",
                accessor: (row) => {
                    const username = userMap[String(row.enter_by)]; // Accessing from userMap directly
                    return username || "Unknown User";
                },
            },
            {
                id: "actions",
                header: "Actions",
                accessor: (row) => (
                    <div>
                        <IconButton
                            aria-label="View details"
                            icon={<InfoIcon />}
                            onClick={() => handleDetailsClick(row.id)}
                            mr={2}
                        />
                        <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            onClick={() => handleDeleteClick(row.id)}
                            colorScheme="red"
                            isLoading={isDeleting}
                        />
                    </div>
                ),
            },
        ],
        [userMap, isDeleting]
    );

    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const [scrollBehavior, setScrollBehavior] = React.useState("inside");

    return (
        <>
            <PageTitle title="Patient Prescriptions" />
            <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
                <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                    gap="20px"
                    mb="20px"
                >
                    <MiniStatistics
                        startContent={
                            <IconBox
                                w="56px"
                                h="56px"
                                bg={boxBg}
                                icon={
                                    <Icon
                                        w="32px"
                                        h="32px"
                                        as={MdPerson}
                                        color={brandColor}
                                    />
                                }
                            />
                        }
                        name="Total Prescriptions"
                        value={
                            isLoading ? (
                                <Spinner size="lg" />
                            ) : err ? (
                                <Text>Error fetching prescriptions</Text>
                            ) : (
                                prescriptionCount
                            )
                        }
                    />
                </SimpleGrid>

                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalBody>
                            <Text>
                                Are you sure you want to delete this
                                prescription?
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                isLoading={isDeleting}
                            >
                                Delete
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                <Flex justify="flex-end" mb="4">
                    <Button
                        leftIcon={<MdAddCircleOutline />}
                        isLoading={results.isLoading}
                        onClick={handleShow}
                    >
                        New Prescription
                    </Button>

                    <Button
                        leftIcon={<MdArrowCircleLeft />}
                        ml="4"
                        onClick={handleBacktoPatientDetails}
                    >
                        Back to Patient Details
                    </Button>
                </Flex>

                {/* Create modal form */}

                <Modal
                    isOpen={show}
                    onClose={handleClose}
                    isCentered
                    {...prescriptions}
                    closeOnOverlayClick={false}
                    closeOnEsc={false}
                    scrollBehavior={scrollBehavior}
                    size="4xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>
                            New Prescription
                            {patient && (
                                <Text fontSize="sm" color="gray.500" mt={2}>
                                    Patient: {patient.name} {patient.surname} | Age: {differenceInYears(new Date(), new Date(patient.date_of_birth))} | Weight: {patient.weight} lbs
                                </Text>
                            )}
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Notes */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl isInvalid={errors.notes}>
                                            <FormLabel>Notes</FormLabel>
                                            <Textarea
                                                {...register("notes", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "notes",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Repeat */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Repeat</FormLabel>
                                            <Checkbox
                                                {...register("repeat")}
                                                label="Yes"
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Hidden Patient Field */}
                                <Input
                                    type="hidden"
                                    {...register("patient")} // Ensure it's required
                                />

                                {/* Hidden Enter By Field */}
                                <Input
                                    value={currentUser?.id || ""}
                                    {...register("enter_by")}
                                    type="hidden"
                                />

                                <Center>
                                    <Box>
                                        <Button
                                            fontSize="sm"
                                            variant="outline"
                                            fontWeight="500"
                                            type="submit"
                                            h="50"
                                            mb="24px"
                                            isLoading={results.isLoading}
                                            loadingText="Loading..."
                                            spinner={<Spinner />}
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </Center>
                            </form>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* End form */}

                <SimpleGrid
                    columns={{ base: 1, md: 1, xl: 1 }}
                    gap="20px"
                    mb="20px"
                >
                    <PaginatedTable columns={columns} initialPageSize={10} />
                </SimpleGrid>
            </Box>
        </>
    );
}

export default Prescriptions;
